// TODO - Type 'any' needs to be fixed.
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { publishDraftShiftsAsync } from "../../../../../../export/shift-scheduler";
import Button from "../../../../../design/button";
import DialogWrapper from "../../../../../design/dialog-wrapper";
import useSchedulerData from "../../../controllers/use-scheduler-data";
import useReviewPublishShiftDialog from "./controllers/use-review-publish-shift-dialog";
import ReviewCheckbox from "./review-checkbox";
import ReviewPublishingShiftsTable from "./review-publish-shifts-table";
import { useReviewPublishColumns, useStyles } from "./utils";

type ReviewPublishingShiftsDialogProps = {
  open: boolean;
  handleClose: () => void;
};

export default function ReviewPublishingShiftsDialog({
  open,
  handleClose,
}: ReviewPublishingShiftsDialogProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const columns = useReviewPublishColumns();

  const {
    publishDraftShiftsAsyncErrorMessage,
    publishDraftShiftsAsyncStatus,
    checkAllowedToPublishData,
    checkAllowedToPublishStatus,
    checkAllowedToPublishErrorMessage,
    isShiftPurposeEnabled,
  } = useSchedulerData();

  const [
    {
      state,
      rows,
      amISure,
      timeLeftByJobs,
      workerCountByShift,
      workerCountByShiftStatus,
      reviewPublishShiftIds,
    },
    { handleShiftSelection, setAmISure, resetShiftCount },
  ] = useReviewPublishShiftDialog();

  const closeDialog = () => {
    handleClose();
    if (resetShiftCount) {
      resetShiftCount();
    }
  };

  const isStateEmpty = state ? state.length < 1 : true;
  const tableData = rows ? rows : [];
  // There is no column selector here, so the Purpose Details column earns its
  // width only when at least one Shift on screen has a purpose that asks for a
  // detail — or already carries an answer from before the question was removed.
  const hasAnyPurposeDetail = tableData.some(
    (row: any) =>
      !!row.purposeDetailLabel ||
      (!!row.shiftPurposeDetail && row.shiftPurposeDetail !== "-")
  );
  const hiddenFields = [
    ...(!isShiftPurposeEnabled ? ["shiftPurpose"] : []),
    ...(!isShiftPurposeEnabled || !hasAnyPurposeDetail
      ? ["shiftPurposeDetail"]
      : []),
  ];
  const finalColumns = hiddenFields.length
    ? columns.filter((col) => !hiddenFields.includes(col.field))
    : columns;

  return (
    <Dialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          closeDialog();
        }
      }}
      aria-labelledby="add-new-shift"
      open={open}
      maxWidth={false}
      PaperProps={{ sx: { minWidth: "80vw" } }}
      TransitionProps={{
        onEntered: () => {
          window.dispatchEvent(new Event("resize"));
        },
      }}
    >
      <DialogWrapper
        dialogTitle={t("scheduler_publish-and-notify-shifts")}
        // dialogSubTitle='Select which shifts should be published. Workers will be notified.'
        dialogContent={
          <ReviewPublishingShiftsTable
            rows={tableData}
            columns={finalColumns}
            handleShiftSelection={handleShiftSelection}
            checkAllowedToPublishData={checkAllowedToPublishData}
            checkAllowedToPublishStatus={checkAllowedToPublishStatus}
            selectedShifts={state}
            timeLeftByJobs={timeLeftByJobs}
            workerCountByShift={workerCountByShift}
            workerCountByShiftStatus={workerCountByShiftStatus}
          />
        }
        contentStyles={{
          padding: "45px 45px 0px 45px",
        }}
        actionButtons={
          <>
            {/* Showing I am sure only if it is opened through header button */}
            {rows && reviewPublishShiftIds.length === 0 && (
              <ReviewCheckbox amISure={amISure} setAmISure={setAmISure} />
            )}
            {publishDraftShiftsAsyncErrorMessage ? (
              <Typography color="secondary">
                {publishDraftShiftsAsyncErrorMessage}.
              </Typography>
            ) : null}
            {!publishDraftShiftsAsyncErrorMessage &&
            checkAllowedToPublishStatus === "error" &&
            checkAllowedToPublishErrorMessage ? (
              <Typography color="secondary">
                {checkAllowedToPublishErrorMessage}
              </Typography>
            ) : null}
            <Button
              color="primary"
              variant="text"
              onClick={closeDialog}
              customStyles={{ marginRight: "16px" }}
            >
              {t("common_cancel")}
            </Button>
            <Button
              onClick={() => {
                if (state) {
                  dispatch(publishDraftShiftsAsync(state));
                }
              }}
              color="primary"
              variant="contained"
              disabled={
                publishDraftShiftsAsyncStatus === "pending" ||
                publishDraftShiftsAsyncStatus === "error" ||
                amISure === false ||
                isStateEmpty
              }
            >
              {publishDraftShiftsAsyncStatus === "pending" ? (
                <div className={classes.btnLabel}>
                  <CircularProgress
                    size={25}
                    className={classes.progressIndicator}
                  />
                  {t("scheduler_publishing-shifts")}
                </div>
              ) : (
                t("scheduler_publish-and-notify")
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
